import * as Sentry from '@sentry/react';

/*
 * The Activity Center's own Sentry project. A DSN is a public ingest key by design — it ships in
 * every client bundle and is safe to read — so it lives here rather than behind an env var that
 * would need a devops change in all four environments before a single error flowed. That matters
 * more than usual here: this repo spent its whole life with Sentry installed and never
 * initialized, so every capture call was silently a no-op. A config step that can be forgotten
 * would rebuild exactly that failure.
 */
const DSN = 'https://99f30442588108556511f06d9407c175@o379338.ingest.us.sentry.io/4511821318193152';

let initialized = false;

/**
 * True when an automated browser is driving the page — Cypress, locally or in CI.
 *
 * `ci/circleci: test_e2e` runs the cypress suite against a production build on every PR, so
 * without this every e2e run would report into the same project as real users. `window.Cypress`
 * is the reliable signal (Cypress injects it); `navigator.webdriver` covers other drivers.
 */
const isAutomatedBrowser = (): boolean =>
  (typeof navigator !== 'undefined' && navigator.webdriver === true)
  || (typeof window !== 'undefined' && 'Cypress' in window);

/**
 * Starts Sentry error reporting. Call once, before anything renders.
 *
 * Deliberately initializes in development too, tagged by `environment`, rather than guarding on
 * NODE_ENV: a guard's failure mode is silence, and silence is the bug this repo just had. Filter
 * `environment:development` in the Sentry UI instead.
 *
 * No tracing integration and no `tracesSampleRate` — this is error monitoring only. Adding
 * `browserTracingIntegration()` later is a performance-quota decision, not a default.
 */
export const initSentry = (): void => {
  if (initialized || isAutomatedBrowser()) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: process.env.NODE_ENV,
    release: process.env.REACT_APP_VERSION,
  });

  initialized = true;
};
